import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    AfterViewInit,
    OnInit,
    OnDestroy
} from "@angular/core";
import {IQuestionEntry, IAnswer, IHint, IQuestion} from "../api/data-model";

import {CssSentence} from "../content/content-view.objects";
import {ClientConfiguration} from "../services/configuration/client.config";
import {QuestionService} from "../services/question/question.service";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {Observable} from "rxjs";
import {Scilearn} from "../constants";
import {ContentCursor} from "../services/content/content.cursor";
import {ComponentBase} from "../base/ComponentBase";
import {Subscription} from "rxjs";
import {StudentConfiguration} from "../services/assignment/student.configuration";
import {EventBus} from "../event/EventBus";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {ScrollService} from "../services/scroll/scroll.service";

// Augment the Answer type with some needed model properties
export interface IAnswerModel extends IAnswer {
    selected:boolean;
    hilited:boolean;
    tried:boolean;
}

@Component({
    selector: "question",
    templateUrl: "./question.component.html",
    styleUrls: ["./question.component.scss"]
})
export class QuestionComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {
    @Input() public questionEntry:IQuestionEntry;

    @Output() public autoClose = new EventEmitter();
    @Output() public userClose = new EventEmitter();
    @Output() public correct = new EventEmitter();
    @Output() public incorrect = new EventEmitter();

    private static WCPM_FOR_TIMING = 130;

    public currentHint:IHint;
    public get presentation():"GR"|"Q" {
        return <"GR"|"Q">this.questionEntry.presentation;
    }

    public selectedSentences:CssSentence[] = null;
    public selectedGrammaticalSentence;
    public allowSubmit:boolean = false;
    public correctlyAnswered:boolean = false;
    public maxHeight: number = 300;

    private get answers():IAnswerModel[] {
        // sometimes answers is not defined in questionEntry, but we can return [] here
        return this.questionEntry && this.questionEntry.answers ? <IAnswerModel[]>this.questionEntry.answers : [];
    }

    private startTime: Date;
    private behavior: QuestionBehavior;
    private questionHighlight = false;
    private shrinkText = false;
    public hintTooltip = false;
    private answersCopyForPlayback: IAnswerModel[] = [];

    constructor(private eventBus: EventBus,
                private audioQueueService: AudioQueueService,
                private questionService:QuestionService,
                private config:ClientConfiguration,
                private studentConfiguration:StudentConfiguration,
                private log:BugsnagLoggingService,
                private contentCursor:ContentCursor,
                private changeDetectorRef: ChangeDetectorRef,
                private scrollService: ScrollService) {
        super("QuestionComponent");
    }

    ngOnInit(): void {
        this.log.log("QuestionComponent ngOnInit");

        this.startTime = new Date();
        switch (this.questionEntry.type) {
            case Scilearn.QUESTIONTYPE_TRUEFALSE:
                this.behavior = new TFQuestionBehavior(this);
                break;
            case Scilearn.QUESTIONTYPE_MULTICHOICE:
                this.behavior = new ChoiceQuestionBehavior(this);
                break;
            case Scilearn.QUESTIONTYPE_PROMPT:
                this.behavior = new NoAnswerQuestionBehavior(this);
                break;
            case Scilearn.QUESTIONTYPE_MULTICHECKS:
                this.behavior = new CheckQuestionBehavior(this);
                break;
            case Scilearn.QUESTIONTYPE_OPENBOOK:
                this.behavior = new OpenBookQuestionBehavior(this, this.contentCursor);
                break;
            default:
        }

        this.resetAnswers();
        this.behavior.init();

        this.playQuestionAudio();
    }

    ngAfterViewInit(): void {
        const container = document.getElementById("questionContainer") || document.getElementById("quizContainer");
        this.maxHeight = Math.floor(window.innerHeight);
        if (!!container) {
            this.maxHeight = container.getBoundingClientRect().height - 15;
        }
        this.changeDetectorRef.detectChanges();
        this.shrinkText = !this.scrollService.checkIfSubmitInView();
    }

    ngOnDestroy(): void {
        this.log.log("QuestionComponent ngOnDestroy");
        this.behavior.cleanup();
    }

    public isAudioAllowed():boolean {
        return this.studentConfiguration.isAudioAllowed;
    }

    public get hasQuestionAudio():boolean {
        return !!(this.questionEntry && this.questionEntry.question.audio);
    }

    public get hasHintAudio():boolean {
        return !!(this.currentHint && this.currentHint.audio);
    }

    public get hasHintText():boolean {
        return !!(this.currentHint && this.currentHint.text);
    }

    public playQuestionAudio():void {
        this.audioQueueService.reset().subscribe(() => {
            this.resetHilites();
            if (this.hasQuestionAudio && this.isAudioAllowed()) {
                this.playAudio(this.questionEntry.question.audio, () => {
                    this.questionHighlight = false;
                    if (this.validAnswers &&
                        (this.questionEntry.type === Scilearn.QUESTIONTYPE_MULTICHECKS
                        || this.questionEntry.type === Scilearn.QUESTIONTYPE_MULTICHOICE
                        || this.questionEntry.type === Scilearn.QUESTIONTYPE_TRUEFALSE)) {
                        this.answersCopyForPlayback = <IAnswerModel[]>this.questionEntry.answers.slice();
                        this.playSequentialAudio(null);
                    }
                });
                this.questionHighlight = true;
            }
        });
    }

    public showHint(userInitiated = false, cb?:Function):void {
        this.hintTooltip = (this.hasHintAudio && this.isAudioAllowed()) || userInitiated;
        if (this.hasHintAudio && this.isAudioAllowed()) {
            this.playAudio(this.currentHint.audio, () => {
                this.deactivateHint();
                if (cb) {
                    cb();
                }
            });
        } else if (userInitiated) {
            let timeout = this.getTimeoutFromText(this.currentHint.text);
            setTimeout(() => {
                this.deactivateHint();
                if (cb) {
                    cb();
                }
            }, timeout);
        } else {
            if (cb) {
                cb();
            }
        }
    }

    private getTimeoutFromText(text: string): number {
        let millisecondsPerWord = 60 * 1000 / QuestionComponent.WCPM_FOR_TIMING;
        let numWords = text.split(" ").length;
        return millisecondsPerWord * numWords;
    }

    public get validAnswers():boolean {
        return this.answers.length > 0;
    }

    private resetAnswers():void {
        for (let answer of this.answers) {
            answer.hilited = false;
            answer.selected = false;
            answer.tried = false;
            if (this.questionEntry.type === Scilearn.QUESTIONTYPE_OPENBOOK) {
                answer.text = "";
            }
        }
    }

    private resetHilites():void {
        this.answers.forEach(x => x.hilited = false);
    }

    public selectAnswer(answer:IAnswer):void {
        this.behavior.select(answer);
    }

    public submitAnswer():void {
        this.answersCopyForPlayback = [];
        this.audioQueueService.reset().subscribe(() => {
            this.resetHilites();
            let endTime = new Date();
            let duration:number = endTime.getTime() - this.startTime.getTime();
            let evaluation:QuestionEvaluation = this.behavior.evaluate();
            let submit:Observable<string> = this.questionService.submitAnswer(
                this.questionEntry,
                evaluation,
                duration,
                () => {});

            let sub = submit.subscribe(() => {
                if (evaluation.correct) {
                    this.onCorrectAnswer();
                } else {
                    this.onIncorrectAnswer();
                }
            });
            this.subscriptions.push(sub);
        });
    }

    private onCorrectAnswer(): void {
        this.log.log("Question answered correctly");
        this.correctlyAnswered = true;
        this.allowSubmit = false;
        this.correct.emit(true);
        this.playCorrectSound(() => {
            this.behavior.onCorrectAnswer();
        });
    }

    private onIncorrectAnswer(): void {
        this.log.log("Question answered incorrectly");
        this.startTime = new Date();
        this.incorrect.emit(true);
        this.playIncorrectSound(() => {
            this.behavior.onIncorrectAnswer();
        });
    }

    public autoCloseQuestion():void {
        this.changeDetectorRef.detectChanges();
        this.audioQueueService.reset().subscribe(() => {
            this.autoClose.emit(true);
            this.questionEntry = undefined;
            this.currentHint = null;
        });
    }

    public userCloseQuestion():void {
        this.audioQueueService.reset().subscribe(() => {
            this.userClose.emit(true);
            this.questionEntry = undefined;
            this.currentHint = null;
        });
    }

    public activateHint(hint: IHint): void {
        this.currentHint = hint;
        this.showHint();
    }

    public deactivateHint(): void {
        this.hintTooltip = false;
    }

    public deactivateClickSentenceHint(): void {
        this.currentHint = null;
        this.hintTooltip = false;
    }

    public activateHintAndClose(hint:IHint): void {
        this.currentHint = hint;
        if (this.currentHint) {
            this.showHint(false, () => { this.autoCloseQuestion(); });
        } else {
            this.autoCloseQuestion();
        }
    }

    public getQuestionTextUIState(): {"question-highlight": boolean, "shrink-text": boolean} {
        return {"question-highlight": this.questionHighlight, "shrink-text": this.shrinkText};
    }

    public playAudio(audio:string, cb?:Function): void {
        this.eventBus.next(new AudioQueueEvent(
            [new AudioQueueItem(this.config.audioUrl + audio, TranslateAudio.NO, cb)], InterruptAudio.YES));
    }

    private playSequentialAudio(cb:Function): void {
        let answer = this.answersCopyForPlayback.shift();
        let next:Function = (this.answersCopyForPlayback.length > 0) ? () => {
            if (answer) {
                answer.hilited = false;
            }
            if (this.answersCopyForPlayback.length > 0) {
                this.playSequentialAudio(cb);
            } else {
                console.log("playback queue cleared, not proceeding");
            }
        } : () => {
            if (answer) {
                answer.hilited = false;
            }
            if (cb) { cb(); }
        };
        if (answer) {
            answer.hilited = true;
            this.changeDetectorRef.detectChanges();
            this.eventBus.next(new AudioQueueEvent(
                [new AudioQueueItem(this.config.audioUrl
                                    + answer.audio, TranslateAudio.NO, next)], InterruptAudio.YES));
        }
    }

    public playbackIsActive(): boolean {
        return (this.answersCopyForPlayback.length > 0) || this.questionHighlight;
    }

    private playCorrectSound(cb:Function): void {
        this.eventBus.next(new AudioQueueEvent(
            [new AudioQueueItem(this.config.audioUrl + "SFX/RA_CORRECT.mp3", TranslateAudio.NO, cb)], InterruptAudio.YES));
    }

    private playIncorrectSound(cb:Function): void {
        this.eventBus.next(new AudioQueueEvent(
            [new AudioQueueItem(this.config.audioUrl + "SFX/RA_INCORRECT.mp3", TranslateAudio.NO, cb)], InterruptAudio.YES));
    }

    public showClickSentenceHintIcon(): boolean {
        return (this.questionEntry && this.questionEntry.type === Scilearn.QUESTIONTYPE_OPENBOOK)
               && !this.selectedSentences;
    }

    public hintIsActive(): boolean {
        return this.showClickSentenceHintIcon() || (this.hintTooltip && !!this.currentHint);
    }
}

export class QuestionEvaluation {
    constructor (public correct:boolean,
                 public userAnswer:string,
                 public correctAnswer:string) {}
}

abstract class QuestionBehavior {
    protected component:QuestionComponent;
    protected question:IQuestion;
    protected entry:IQuestionEntry;
    protected answers:IAnswerModel[];
    public retryTimes:number = 0;

    constructor(q:QuestionComponent) {
        this.component = q;
        this.entry = q.questionEntry;
        this.question = q.questionEntry.question;
        this.answers = <IAnswerModel[]>q.questionEntry.answers;
    }

    init(): void {
    };

    select(value:IAnswer|IAnswer[]|CssSentence): void {
    };

    submit(): void {
    };

    evaluate():QuestionEvaluation {
        return undefined;
    };

    cleanup():void {
    };

    onCorrectAnswer():void {
    };

    onIncorrectAnswer():void {
    };

    getAnswerHint(answer:IAnswerModel, level:number):IHint {
        return answer.hints && answer.hints.find(h => parseInt(h.level, 10) === level);
    }

    getQuestionHint(level:number):IHint {
        return this.entry.hints && this.entry.hints.find(h => parseInt(h.level, 10) === level);
    }

    protected static isTrue(value:string|boolean):boolean {
        return value === true || value === "true";
    }
}

class ChoiceQuestionBehavior extends QuestionBehavior {

    select(answer:IAnswerModel): void {
        if (answer.tried && !answer.correct) {
            return;
        }
        for (let a of <IAnswerModel[]>this.entry.answers) {
            a.selected = (a === answer);
        }
        this.component.allowSubmit = true;
    }

    onCorrectAnswer():void {
        let answer = this.answers.find(x => x.correct);
        let hint = this.getAnswerHint(answer, this.retryTimes === 0 ? 0 : 4);
        this.component.activateHintAndClose(hint);
    };

    onIncorrectAnswer():void {
        let answer = this.answers.find(x => x.selected);
        let hint = this.getAnswerHint(answer, ++this.retryTimes);
        this.component.activateHint(hint);
        answer.selected = false;
        this.component.allowSubmit = false;
    };

    evaluate(): QuestionEvaluation {
        let userAnswer = this.answers.find(x => x.selected);
        if (userAnswer) {
            userAnswer.tried = true;
        }
        let correctAnswer = this.answers.find(x => QuestionBehavior.isTrue(x.correct));
        let correct = userAnswer === correctAnswer;

        return new QuestionEvaluation(correct,
            this.answers.indexOf(userAnswer).toString(), this.answers.indexOf(correctAnswer).toString());
    }
}

class TFQuestionBehavior extends ChoiceQuestionBehavior {
}

export class CheckQuestionBehavior extends QuestionBehavior {

    private omitAnswerCount:number;
    wrongAnswerCount:number;

    init(): void {
        super.init();
        this.component.allowSubmit = true;
    }

    select(answer:IAnswerModel): void {
        if (!answer.tried) {
            answer.selected = !answer.selected;
        }
    }

    onCorrectAnswer():void {
        let hint = this.getQuestionHint(this.determineLevelForCorrectAnswer());
        this.component.activateHintAndClose(hint);
    };

    onIncorrectAnswer():void {
        this.retryTimes++;
        let hint:IHint = this.getQuestionHint(this.determineLevelForIncorrectAnswer());
        this.component.activateHint(hint);
    };

    determineLevelForIncorrectAnswer():number {
        let level:number = this.wrongAnswerCount === 0 ? 7 : this.retryTimes === 0 ? 6 : 5;
        return level;
    }

    determineLevelForCorrectAnswer():number {
        let level:number = this.retryTimes === 0 ? 0 : 4;
        return level;
    }

    evaluate():QuestionEvaluation {
        this.omitAnswerCount = 0;
        this.wrongAnswerCount = 0;

        let userAnswer = this.answers.filter(x => x.selected).map((x) => this.answers.indexOf(x).toString()).join(",");
        let correctAnswer = this.answers.filter(x => QuestionBehavior.isTrue(x.correct))
            .map((x) => this.answers.indexOf(x).toString()).join(",");
        console.log("multiple check userAnswer: " + userAnswer + " correctAnswer: " + correctAnswer);

        for (let answer of this.answers) {
            if (answer.selected) {
                answer.tried = true;
                if (!QuestionBehavior.isTrue(answer.correct)) {
                    this.wrongAnswerCount++;
                    answer.selected = false;
                }
            } else if (QuestionBehavior.isTrue(answer.correct)) {
                this.omitAnswerCount++;
            }
        }

        let noWrongAnswer = this.wrongAnswerCount === 0;
        let noOmittedAnswer = this.omitAnswerCount === 0;
        let correct:boolean = noWrongAnswer && noOmittedAnswer;

        return new QuestionEvaluation(correct, userAnswer, correctAnswer);
    }
}

export class OpenBookQuestionBehavior extends QuestionBehavior {

    private scSubscription:Subscription;

    constructor(q:QuestionComponent, private contentCursor:ContentCursor) {
        super(q);
    }

    onCorrectAnswer():void {
        let answer = this.answers[0];
        let hintNumber = this.retryTimes === 0 ? 0 : 4;
        let hint = this.getAnswerHint(answer, hintNumber);
        this.component.activateHintAndClose(hint);
    };

    onIncorrectAnswer():void {
        let answer = this.answers[0];
        this.retryTimes++;
        let hintNumber = this.retryTimes > 3 ? 3 : this.retryTimes;
        let hint = this.getAnswerHint(answer, hintNumber);
        this.component.activateHint(hint);
    };

    init(): void {
        super.init();
        this.scSubscription = this.contentCursor.sentenceChanged.subscribe(sentence => this.select(sentence));
    }

    cleanup(): void {
        super.cleanup();
        this.scSubscription.unsubscribe();
        if (this.component.selectedSentences) {
            this.component.selectedSentences.forEach((sentence) => sentence.highlightOpenBook(false));
        }
    }

    evaluate():QuestionEvaluation {
        let answer = this.answers[0];
        let selected = this.component.selectedSentences;
        let answerList = answer.answerSentenceId.split(/[+/]/);
        let answerHasAlternatives = answer.answerSentenceId.indexOf("/") >= 0;
        let correct;
        if (answerHasAlternatives) {
            correct = answerList.indexOf(selected[0].id) >= 0;
            answerList = answerList.slice(0, 1);
        } else {
            let correctAnswerJSON = JSON.stringify(answerList.sort());
            let userAnswerJSON = JSON.stringify(selected.map(s => s.id).sort());
            console.log("correctAnswerJSON:" + correctAnswerJSON + " userAnswerJSON:" + userAnswerJSON);
            correct = correctAnswerJSON === userAnswerJSON;
        }
        let correctAnswer = "";
        if (!correct) {
            let correctSentences:CssSentence[] = answerList.map(x => this.contentCursor.selection.sentenceFromId(x));
            if (correctSentences) {
                correctSentences.forEach((correctSentence) => {
                    correctAnswer += correctSentence.words.map(x => [x.prefix, x.word, x.separator].join("")).join("");
                });
            }
        }
        let userAnswer = this.component.selectedGrammaticalSentence;
        return new QuestionEvaluation(correct, userAnswer, correctAnswer);
    }

    select(sentence:CssSentence):void {
        this.component.allowSubmit = true;
        if (this.component.selectedSentences) {
            this.component.selectedSentences.forEach((s) => s.highlightOpenBook(false));
        }
        this.component.selectedSentences = this.grammaticalSentenceContaining(sentence);
        this.component.selectedSentences.forEach((s) => s.highlightOpenBook(true));
        this.component.selectedGrammaticalSentence = "";
        this.component.selectedSentences.forEach((s) => {
            this.component.selectedGrammaticalSentence +=
                s.words.map(x => [x.prefix, x.word, x.separator].join("")).join("");
        });
        this.component.deactivateClickSentenceHint();
    }

    private grammaticalSentenceContaining(sentence: CssSentence):CssSentence[] {
        if (!sentence) {
            return [];
        }
        // find the sentence that starts the full grammatical sentence;
        let startSentence = sentence;
        while (startSentence && this.contentCursor.selection.previousSentence(startSentence) &&
               !this.hasSentenceBoundary(this.contentCursor.selection.previousSentence(startSentence))) {
            startSentence = this.contentCursor.selection.previousSentence(startSentence);
        }
        // find sentence that ends the full grammatical sentence;
        let endSentence = sentence;
        while (endSentence && !this.hasSentenceBoundary(endSentence) &&
               this.contentCursor.selection.nextSentence(endSentence)) {
            endSentence = this.contentCursor.selection.nextSentence(endSentence);
        }
        let sentences = [startSentence];
        let currentSentence = startSentence;
        while (currentSentence && (currentSentence !== endSentence)) {
            currentSentence = this.contentCursor.selection.nextSentence(currentSentence);
            sentences.push(currentSentence);
        }
        return sentences;
    }

    private hasSentenceBoundary(sentence: CssSentence): boolean {
        // any of these characters: ".?!—" qualify as sentence ending punctuation according to raweb
        if (!sentence || !sentence.words || !sentence.words.length) {
            return true;
        }
        let endsWithSentencePunctuation = !!sentence.words[sentence.words.length - 1].separator.match(/[?.!—]/);
        let isHeading = (sentence.parentText().formats &&
                         ((sentence.parentText().formats.indexOf("heading") >= 0) ||
                         sentence.parentText().formats.indexOf("subheading") >= 0));
        return endsWithSentencePunctuation || isHeading;
    }
}

class NoAnswerQuestionBehavior extends QuestionBehavior {

    init(): void {
        super.init();
        this.component.allowSubmit = true;
    }

    evaluate():QuestionEvaluation {
        return new QuestionEvaluation(true, "", "");
    }

    onCorrectAnswer():void {
        this.component.autoCloseQuestion();
    };
}
